import React, {
  FC,
  MouseEventHandler,
  ReactNode,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react'

import { css, cx } from '@linaria/core'
import { styled } from '@linaria/react'

import { Loader } from '../../ui/Loader'
import { colors } from '../../styles/colors'
import { VoidHandler } from './BottomSheet'

import { BOTTOM_SHEET_KEY, BOTTOM_SHEET_VALUE } from './BottomSheet.constants'
import { BottomSheetTitle } from './BottomSheetTitle'

const fullStopPropagation = (event: React.MouseEvent | React.TouchEvent) => {
  event.stopPropagation()
  event.preventDefault()
}

const isScrolledToBottom = (element: HTMLElement) => {
  return element.scrollHeight - element.scrollTop <= element.clientHeight + 1
}

export const BottomSheetInner: FC<{
  title?: ReactNode
  subtitle?: ReactNode
  data: unknown
  open: boolean | null
  setOpen: (open: boolean) => void
  onUnmounted: VoidHandler
  /**
   * Чтобы список не сжимался по высоте.
   * Например при фильтрации списка высота контейнера будет уменьшаться.
   */
  noHeightShrink?: boolean
  /** Если нужна максимально доступная высота для мобилы, вместо высоты по контенту */
  mobileMaxHeight?: boolean
  isIOS: boolean
  children:
    | ReactNode
    | ((params: {
        data: unknown
        contentNode: HTMLDivElement | null
      }) => ReactNode)
  dataName?: string
  footer?: ReactNode
}> = ({
  title,
  subtitle,
  data,
  children,
  footer,
  open,
  setOpen,
  onUnmounted,
  mobileMaxHeight,
  isIOS,
  dataName,
}) => {
  const titleRef = useRef<HTMLDivElement | null>(null)
  const overlayRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [contentNode, setContentNode] = useState<HTMLDivElement | null>(null)
  const movingRef = useRef<HTMLDivElement | null>(null)
  const domRect = useRef<DOMRect | undefined>()
  const positionYRef = useRef<number>(0)
  const scrollTopRef = useRef<number>(0)
  const startingScrollTopRef = useRef<number>(0)
  const offsetRef = useRef<number>(0)
  const isDraggingRef = useRef<boolean>(false)
  const isScrolledRef = useRef<boolean>(false)
  const [mounted, setMounted] = useState<boolean>(false)

  // Очистка атрибута при размонтировании
  useEffect(() => {
    return () => {
      document.documentElement.removeAttribute(BOTTOM_SHEET_KEY)
    }
  }, [])

  useEffect(() => {
    if (open === null) {
      return
    }

    if (open && !mounted) {
      setMounted(true)
      return
    }

    requestAnimationFrame(() => {
      if (!movingRef.current || !overlayRef.current) {
        return
      }

      if (open) {
        domRect.current = movingRef.current.getBoundingClientRect()
        overlayRef.current.style.opacity = OPACITY_SHOW
        movingRef.current.style.transform = TRANSFORM_SHOW
        // Устанавливаем атрибут для блокировки скролла
        document.documentElement.setAttribute(
          BOTTOM_SHEET_KEY,
          BOTTOM_SHEET_VALUE,
        )
      } else {
        overlayRef.current.style.opacity = OPACITY_HIDE
        movingRef.current.style.transform = TRANSFORM_HIDE
        // Убираем атрибут при закрытии
        document.documentElement.removeAttribute(BOTTOM_SHEET_KEY)

        // Закрываем с помощью таймаута, потому что на iOS не вызывается событие transitionend
        window.setTimeout(() => {
          setMounted(false)
          requestAnimationFrame(() => onUnmounted(true))
        }, ANIMATION_DURATION_MS)
      }
    })
  }, [open, mounted, onUnmounted])

  useEffect(() => {
    if (!movingRef.current || !overlayRef.current) {
      return
    }

    const node = movingRef.current

    const setOverlayColor = (positionY: number) => {
      if (!domRect.current || !overlayRef.current) {
        return
      }
      const shiftPercent = positionY / domRect.current.height
      const opacity = 1 - shiftPercent - 0.6
      overlayRef.current.style.opacity = String(opacity)
    }

    const handleTouchStart = (event: TouchEvent) => {
      if (!node || !domRect.current) {
        return
      }
      node.style.transition = TRANSITION_MOVE
      domRect.current = node.getBoundingClientRect()
      isDraggingRef.current = true
      const clientY = event.touches[0].clientY
      offsetRef.current = clientY - domRect.current.top
      isScrolledRef.current = contentNode ? contentNode.scrollTop !== 0 : false
      startingScrollTopRef.current = scrollTopRef.current
    }

    const handleTouchMove = (event: TouchEvent) => {
      if (!contentNode || !node) {
        return
      }

      const clientY = event.touches[0].clientY
      const positionY = Math.round(clientY - offsetRef.current)

      const overscrollUp = contentNode.scrollTop === 0 && positionY > 0
      const overscrollDown = isScrolledToBottom(contentNode) && positionY < 0

      if (isIOS && (overscrollUp || overscrollDown)) {
        contentNode.style.overflowY = 'hidden'
        // Чтобы не срабатывал pull to refresh на iOS 15 и 16
        // И чтобы нельзя было оттянуть контент вверх, что приводит к багу со скроллом
        event.preventDefault()
      }

      const scrollingContent =
        isScrolledRef.current &&
        event.target instanceof HTMLElement &&
        contentNode.contains(event.target)

      if (
        !isDraggingRef.current ||
        scrollingContent /** Если это скролл контента - не перемещаем */ ||
        positionY < 0 /** Не даем скроллить выше дозволенного. */ ||
        contentNode.scrollTop < 0 /** Отрицательный скролл iOS */
      ) {
        return
      }

      setOverlayColor(positionY)
      positionYRef.current = positionY
      node.style.transition = TRANSITION_MOVE
      node.style.transform = `translate3d(0, ${positionY}px, 0)`
    }

    const handleTouchEnd = (event: TouchEvent) => {
      if (!contentNode || !node) {
        return
      }
      contentNode.style.overflowY = 'scroll'
      node.style.transition = TRANSITION_START

      const exception =
        event.target instanceof HTMLElement &&
        contentNode.contains(event.target)

      // Кейс для iOS: пользователь активно оттягивает прокручивающийся список вниз
      if (
        startingScrollTopRef.current < 0 &&
        scrollTopRef.current < 0 &&
        scrollTopRef.current < startingScrollTopRef.current
      ) {
        setOpen(false)
        return
      }

      if (scrollTopRef.current < -50) {
        setOpen(false)
        return
      }

      if (!isDraggingRef.current || (isScrolledRef.current && exception)) {
        isScrolledRef.current = false
        return
      }

      if (!domRect.current || !movingRef.current) {
        return
      }
      const clientY = event.changedTouches[0].clientY
      const positionY = clientY - offsetRef.current
      const height = domRect.current.height
      const dragged = positionY > height / 5
      const offsetY = dragged ? height : 0
      setOverlayColor(offsetY)

      if (dragged) {
        setOpen(false)
      } else {
        movingRef.current.style.transform = TRANSFORM_SHOW
      }

      isDraggingRef.current = false
    }

    node.addEventListener('touchstart', handleTouchStart)
    /**
     * Вешаем touchmove и touchend на document,
     * чтобы ловить свайп даже если палец уходит за пределы элемента (movingRef),
     * например при длинных движениях или жестах на мобильных устройствах.
     */
    document.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    })
    document.addEventListener('touchend', handleTouchEnd)

    return () => {
      node.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
    }
  }, [contentNode, isIOS, mounted, setOpen])

  const handleSetScrollableNode = useCallback((node: HTMLDivElement | null) => {
    setContentNode(node)
  }, [])

  if (!mounted) {
    return
  }

  const handleCloseClick: MouseEventHandler<HTMLButtonElement> = event => {
    event.preventDefault()
    fullStopPropagation(event)
    setOpen(false)
  }
  const handleOverlayClick: MouseEventHandler<HTMLDivElement> = event => {
    event.preventDefault()
    fullStopPropagation(event)
    setOpen(false)
  }

  /**
   * Заголовок показываем только если есть заголовок или подзаголовок
   */
  const emptyTitle = Boolean(title || subtitle)
  const titleContainerVisible = emptyTitle

  const handleContentScroll = (event: React.UIEvent<HTMLElement>) => {
    if (!titleRef.current) {
      return
    }

    const target = event.currentTarget
    scrollTopRef.current = target.scrollTop

    titleRef.current.style.boxShadow =
      target.scrollTop > 0 ? '0 1px 0 0 var(--color-border-secondary)' : 'none'
  }

  return (
    <BottomSheetContainer data-name={dataName}>
      <BottomSheetOverlay ref={overlayRef} onClick={handleOverlayClick} />

      <Moving ref={movingRef} onClick={handleOverlayClick}>
        <BottomSheetInnerContainer
          ref={containerRef}
          onClick={fullStopPropagation}
          className={cx(mobileMaxHeight && mobileMaxHeightCss)}
        >
          <Handle />

          {titleContainerVisible && (
            <BottomSheetTitleContainer ref={titleRef}>
              <BottomSheetTitle
                title={title}
                onCloseClick={handleCloseClick}
                mobile={true}
              />
              {subtitle && <Subtitle>{subtitle}</Subtitle>}
            </BottomSheetTitleContainer>
          )}
          <ScrollContainer
            ref={handleSetScrollableNode}
            onScroll={handleContentScroll}
          >
            <BottomSheetContentInner>
              {(typeof children === 'function'
                ? children({ data, contentNode })
                : children) || <Loader />}

              {footer}
            </BottomSheetContentInner>
          </ScrollContainer>
        </BottomSheetInnerContainer>
      </Moving>
    </BottomSheetContainer>
  )
}

const ANIMATION_DURATION_MS = 300
const TRANSITION_START = `transform ${ANIMATION_DURATION_MS}ms ease-out`
const TRANSITION_MOVE = `transform ${30}ms linear`
const TRANSFORM_SHOW = 'translate3d(0, 0, 0)'
const TRANSFORM_HIDE = 'translate3d(0, 100%, 0)'
const OPACITY_SHOW = '0.4'
const OPACITY_HIDE = '0'

const zIndex = {
  default: 9999,
}

export const BottomSheetContainer = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.default};
`
const Handle = styled.div`
  position: absolute;
  top: -12px;
  width: 48px;
  height: 4px;
  border-radius: 2.5px;
  opacity: 0.48;
  background: var(--color-text-secondary);
  background-blend-mode: overlay;
`
export const BottomSheetOverlay = styled.div`
  position: fixed;
  inset: 0;
  touch-action: none;
  opacity: ${OPACITY_HIDE};
  transition: opacity ${ANIMATION_DURATION_MS}ms ease-out;
  will-change: opacity;
  background-color: ${colors.shadowDark};
`
const Moving = styled.div`
  position: absolute;
  inset: 0;
  touch-action: manipulation;
  transform: ${TRANSFORM_HIDE};
  will-change: transform;
  transition: ${TRANSITION_START};
`
export const BottomSheetTitleContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  text-align: center;
  background: var(--color-background-primary);
  border-radius: 32px 32px 0 0;
  transition: box-shadow ${ANIMATION_DURATION_MS}ms;
  will-change: box-shadow;
  padding: 32px 16px 12px 16px;
`
const Subtitle = styled.div`
  margin-top: 24px;
`
export const BottomSheetInnerContainer = styled.div`
  position: absolute;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-height: 90%;
  border-radius: 32px 32px 0 0;
  background: white;
`
const BottomSheetContentInner = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding: 24px;
`

const ScrollContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
`

const mobileMaxHeightCss = css`
  height: 100%;
`
